import {
  UseFormHandleSubmit,
  UseFormReturn,
  useForm,
} from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { CompanyContactInformationSerializer } from '@bcba/serializers';
import { SelectOption } from '@bcba/types';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useLayoutEffect, useMemo } from 'react';
import { usePendingChangesAlert } from '../../../../contexts/hooks';
import { updateCompanyContactInfo } from '../../../../services/company';
import { UpsertCompanyContactInformationDto } from '../../../../services/company/dto';
import { getProvinceLocalities } from '../../../../services/provinces/getLocalities';
import { getProvinces } from '../../../../services/provinces/getProvinces';
import { useDigitalDossierActionButtons } from '../../hooks/useDigitalDossierActionButtons';

interface ContactInfoSectionProps {
  handleSubmit: UseFormHandleSubmit<
    CompanyContactInformationSerializer,
    undefined
  >;
  onSubmit: (
    dto: UpsertCompanyContactInformationDto & {
      localityName: string;
      provinceName: string;
    },
  ) => void;
  formMethods: UseFormReturn<
    CompanyContactInformationSerializer,
    any,
    undefined
  >;
  provinces?: SelectOption[];
  localities?: SelectOption[];
  defaultProvinceInputValue: string;
  isLoadingLocalities: boolean;
  actionButtons: ReactNode;
}

const resolver = classValidatorResolver(UpsertCompanyContactInformationDto);

export const useContactInfoSection = ({
  defaultValues,
}: {
  defaultValues: CompanyContactInformationSerializer;
}): ContactInfoSectionProps => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToastsHook();

  const queryClient = useQueryClient();

  const formMethods = useForm({
    defaultValues,
    resolver,
  });

  usePendingChangesAlert(formMethods.formState.isDirty);

  const { handleSubmit, watch, setValue, setError, reset } = formMethods;
  const selectedProvinceUuid = watch('provinceUuid');

  const onSubmit = (
    dto: UpsertCompanyContactInformationDto & {
      localityName: string;
      provinceName: string;
    },
  ) => {
    const { localityName, provinceName, ...rest } = dto;

    mutate({ ...rest });
  };

  const { data: provinces } = useQuery({
    queryKey: ['province'],
    queryFn: getProvinces,
  });

  const mapProvince = useMemo(
    () =>
      provinces
        ? provinces?.map((province) => {
            return { value: province.uuid, label: province.name };
          })
        : [
            {
              value: defaultValues?.provinceUuid,
              label: defaultValues?.provinceName,
            },
          ],
    [defaultValues?.provinceName, defaultValues?.provinceUuid, provinces],
  );

  const {
    data: localities,
    isLoading: isLoadingLocalities,
    refetch: refetchLocalities,
  } = useQuery({
    queryKey: [`localities-province-${selectedProvinceUuid}`],
    queryFn: async () => await getProvinceLocalities(selectedProvinceUuid, ''),
    enabled: Boolean(selectedProvinceUuid),
  });

  const mapLocalities = useMemo(
    () =>
      localities
        ? localities?.map((province) => {
            return { value: province.uuid, label: province.name };
          })
        : [
            {
              value: defaultValues?.localityUuid,
              label: defaultValues?.localityName,
            },
          ],
    [defaultValues?.localityName, defaultValues?.localityUuid, localities],
  );

  const { mutate, isPending: isLoadingMutate } = useMutation({
    mutationFn: updateCompanyContactInfo,
    onSuccess: (data) => {
      showSuccessToast({
        title: t('companyContactInformation.toast.success.title'),
      });
      queryClient.setQueryData(['contactInfo'], data);
      reset(data, { keepDirty: false });
    },
  });

  const { actionButtons } = useDigitalDossierActionButtons({
    isLoadingSave: isLoadingMutate,
    formHasChanges: formMethods.formState.isDirty,
    thisSectionNeedsApproval: false,
  });

  useLayoutEffect(() => {
    if (selectedProvinceUuid && formMethods.formState.isDirty) {
      setError('localityUuid', {
        type: 'custom',
        message: t('companyContactInformation.form.error.validLocality'),
      });
      setValue('localityUuid', '');
      setValue('localityName', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvinceUuid]);

  useEffect(() => {
    if (selectedProvinceUuid && formMethods.formState.isDirty)
      refetchLocalities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvinceUuid]);

  return {
    handleSubmit,
    onSubmit,
    formMethods,
    provinces: mapProvince,
    localities: mapLocalities,
    defaultProvinceInputValue: watch('provinceName'),
    isLoadingLocalities,
    actionButtons,
  };
};

export default useContactInfoSection;
